import React, { useId, useMemo, useRef } from "react";

import { getBodyTree } from "../../../stores/sceneActions/bodyUtils";

const flattenBodyTreeRows = (nodes, level = 1, parentUuid = null) =>
  nodes.flatMap((node, index) => {
    const children = Array.isArray(node.children) ? node.children : [];
    return [
      {
        node,
        level,
        parentUuid,
        posInSet: index + 1,
        setSize: nodes.length,
      },
      ...flattenBodyTreeRows(children, level + 1, node.uuid),
    ];
  });

export default function SceneGraphAccessibleBodyTree({
  bodies,
  effectiveWorldBodies,
  selectedBody,
  selectBodyForSceneGraphTarget,
}) {
  const treeId = useId();
  const itemRefs = useRef(new Map());
  const bodyTree = useMemo(
    () => getBodyTree({ bodies, worldBodies: effectiveWorldBodies }),
    [bodies, effectiveWorldBodies]
  );
  const rows = useMemo(() => flattenBodyTreeRows(bodyTree), [bodyTree]);
  const rowIndexByUuid = useMemo(
    () => new Map(rows.map((row, index) => [row.node.uuid, index])),
    [rows]
  );
  const selectedIndex = selectedBody ? rowIndexByUuid.get(selectedBody) : undefined;
  const tabbableIndex = selectedIndex ?? 0;

  const focusRow = (index) => {
    const row = rows[index];
    if (!row?.node.uuid) return;
    itemRefs.current.get(row.node.uuid)?.focus();
  };

  const selectBody = (bodyUuid) => {
    selectBodyForSceneGraphTarget(bodyUuid, "sceneTree");
  };

  const handleTreeItemKeyDown = (event, rowIndex, row) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusRow(Math.min(rows.length - 1, rowIndex + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        focusRow(Math.max(0, rowIndex - 1));
        break;
      case "ArrowRight":
        if (row.node.children?.length) {
          event.preventDefault();
          focusRow(rowIndex + 1);
        }
        break;
      case "ArrowLeft":
        if (row.parentUuid) {
          event.preventDefault();
          focusRow(rowIndexByUuid.get(row.parentUuid) ?? rowIndex);
        }
        break;
      case "Home":
        event.preventDefault();
        focusRow(0);
        break;
      case "End":
        event.preventDefault();
        focusRow(rows.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        selectBody(row.node.uuid);
        break;
      default:
        break;
    }
  };

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute left-3 top-3 z-30 max-h-[calc(100%-1.5rem)] w-72 overflow-auto rounded-md border border-gray-600 bg-gray-950/95 p-2 text-sm text-gray-100 opacity-0 shadow-xl transition-opacity focus-within:pointer-events-auto focus-within:opacity-100">
      <div
        id={`${treeId}-label`}
        className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-gray-400"
      >
        Scene bodies
      </div>
      <div role="tree" aria-labelledby={`${treeId}-label`} aria-multiselectable="false">
        {rows.map((row, rowIndex) => {
          const bodyUuid = row.node.uuid;
          const selected = selectedBody === bodyUuid;
          const hasChildren = row.node.children?.length > 0;
          return (
            <div
              key={bodyUuid}
              ref={(element) => {
                if (element) {
                  itemRefs.current.set(bodyUuid, element);
                } else {
                  itemRefs.current.delete(bodyUuid);
                }
              }}
              role="treeitem"
              aria-level={row.level}
              aria-posinset={row.posInSet}
              aria-setsize={row.setSize}
              aria-selected={selected}
              aria-expanded={hasChildren ? true : undefined}
              tabIndex={rowIndex === tabbableIndex ? 0 : -1}
              className={`cursor-pointer rounded px-2 py-1 outline-none focus-visible:ring-2 focus-visible:ring-sky-300 ${
                selected ? "bg-sky-600 text-white" : "text-gray-100 hover:bg-gray-800"
              }`}
              style={{ paddingLeft: `${8 + (row.level - 1) * 16}px` }}
              onClick={() => selectBody(bodyUuid)}
              onKeyDown={(event) => handleTreeItemKeyDown(event, rowIndex, row)}
            >
              {row.node.name || bodyUuid}
            </div>
          );
        })}
      </div>
    </div>
  );
}
